/**
 * Camada de anotações manuais sobre a contestada (E36.F3). // ux-neutrality:allow doc-comment
 *
 * SVG com viewBox nas dimensões naturais da imagem + preserveAspectRatio=meet // ux-neutrality:allow doc-comment
 * (mesma tática do ContestedMarkerOverlay): a camada escala junto com o <img> // ux-neutrality:allow doc-comment
 * e com o zoom do ZoomPanViewport. Geometria persistida é NORMALIZADA à imagem // ux-neutrality:allow doc-comment
 * (lib/annotation_geometry) — zoom nunca distorce a anotação. // ux-neutrality:allow doc-comment
 *
 * Gestos: com ferramenta ativa, arrasto desenha (seta cauda→ponta; círculo // ux-neutrality:allow doc-comment
 * centro→borda) na superfície SVG; sem ferramenta, a camada é transparente a // ux-neutrality:allow doc-comment
 * eventos e só os TRAÇOS das anotações são clicáveis (selecionar) e arrastáveis // ux-neutrality:allow doc-comment
 * (mover, com listeners de janela — o mouse sai do traço durante o arrasto). // ux-neutrality:allow doc-comment
 * Esc cancela o desenho em curso. Arrasto mínimo evita cliques acidentais // ux-neutrality:allow doc-comment
 * (mesma lição da régua, E27.F1.C02). // ux-neutrality:allow doc-comment
 */
import { useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import {
  annotationStrokePx,
  arrowFromDrag,
  arrowHeadLenPx,
  arrowHeadPoints,
  circleFromDrag,
  clientToImageNorm,
  translateGeometry,
  type ImageSize,
  type NormPoint,
} from '@/lib/annotation_geometry';
import {
  ANNOTATION_COLOR_HEX,
  isArrowGeometry,
  type AnnotationColor,
  type AnnotationGeometryData,
  type AnnotationTool,
  type MesaAnnotationData,
} from '@/lib/mesa_annotations';

/** Arrasto mínimo em px de TELA para o gesto valer (clique acidental ignora). */
export const ANNOTATION_MIN_DRAG_SCREEN_PX = 5;

export interface AnnotationLayerProps {
  annotations: MesaAnnotationData[];
  drawTool: AnnotationTool | null;
  drawColor: AnnotationColor;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onDraw: (tool: AnnotationTool, color: AnnotationColor, geometry: AnnotationGeometryData) => void;
  onMove: (id: string, geometry: AnnotationGeometryData) => void;
  /** Régua ativa: os traços ficam inertes — o arrasto sobre eles é da medição. */
  shapesInert?: boolean;
  /** Dimensões naturais da contestada — testes injetam; produção mede do <img>. */
  naturalSize?: ImageSize | null;
}

interface Draft {
  start: NormPoint;
  current: NormPoint;
  downClient: { x: number; y: number };
}

interface Moving {
  id: string;
  origin: AnnotationGeometryData;
  preview: AnnotationGeometryData;
  startNorm: NormPoint;
  downClient: { x: number; y: number };
}

type ShapeVariant = 'solid' | 'dashed' | 'halo';

interface ShapeProps {
  geometry: AnnotationGeometryData;
  color: string;
  size: ImageSize;
  /** `dashed` = rascunho do gesto; `halo` = aura larga da seleção. */
  variant?: ShapeVariant;
}

function ShapeSvg({ geometry, color, size, variant = 'solid' }: ShapeProps): JSX.Element {
  // Traço fino na escala natural (~2px) — mesma régua da mesa (annotation_geometry).
  const base = annotationStrokePx(size);
  const stroke = variant === 'halo' ? base * 3 : base;
  const extra: React.SVGAttributes<SVGElement> = {};
  if (variant === 'dashed') extra.strokeDasharray = `${base * 4} ${base * 3}`;
  if (variant === 'halo') extra.opacity = 0.35;
  if (isArrowGeometry(geometry)) {
    const headLen = arrowHeadLenPx(size);
    return (
      <>
        <line
          x1={geometry.x1 * size.width}
          y1={geometry.y1 * size.height}
          x2={geometry.x2 * size.width}
          y2={geometry.y2 * size.height}
          stroke={color}
          strokeWidth={stroke}
          {...extra}
        />
        <polygon points={arrowHeadPoints(geometry, size, headLen)} fill={color} {...extra} />
      </>
    );
  }
  return (
    <circle
      cx={geometry.cx * size.width}
      cy={geometry.cy * size.height}
      r={geometry.r * size.width}
      fill="none"
      stroke={color}
      strokeWidth={stroke}
      {...extra}
    />
  );
}

/** Cópia invisível e larga da geometria — alvo de clique generoso p/ traço fino. */
function HitSvg({ geometry, size }: Omit<ShapeProps, 'color'>): JSX.Element {
  const hit = Math.max(14, Math.round(Math.max(size.width, size.height) * 0.02));
  if (isArrowGeometry(geometry)) {
    return (
      <line
        x1={geometry.x1 * size.width}
        y1={geometry.y1 * size.height}
        x2={geometry.x2 * size.width}
        y2={geometry.y2 * size.height}
        stroke="transparent"
        strokeWidth={hit}
      />
    );
  }
  return (
    <circle
      cx={geometry.cx * size.width}
      cy={geometry.cy * size.height}
      r={geometry.r * size.width}
      fill="none"
      stroke="transparent"
      strokeWidth={hit}
    />
  );
}

export function AnnotationLayer({
  annotations,
  drawTool,
  drawColor,
  selectedId,
  onSelect,
  onDraw,
  onMove,
  shapesInert = false,
  naturalSize = null,
}: AnnotationLayerProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [measured, setMeasured] = useState<ImageSize | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [moving, setMovingState] = useState<Moving | null>(null);
  // Espelho do gesto de mover p/ commit determinístico no mouseup de janela
  // (sem efeito colateral dentro de updater de estado).
  const movingRef = useRef<Moving | null>(null);
  const size = naturalSize ?? measured;

  function setMoving(next: Moving | null): void {
    movingRef.current = next;
    setMovingState(next);
  }

  // Mede as dimensões naturais do <img> irmão (produção; testes injetam).
  useEffect(() => {
    if (naturalSize !== null) return undefined;
    const img = wrapperRef.current?.parentElement?.querySelector('img') ?? null;
    if (img === null) return undefined;
    function measure(): void {
      if (img !== null && img.naturalWidth > 0) {
        setMeasured({ width: img.naturalWidth, height: img.naturalHeight });
      }
    }
    measure();
    img.addEventListener('load', measure);
    return () => img.removeEventListener('load', measure);
  }, [naturalSize]);

  function toNorm(clientX: number, clientY: number): NormPoint | null {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || size === null) return null;
    return clientToImageNorm(clientX, clientY, rect, size);
  }

  // Esc cancela o desenho em curso (paridade com a régua).
  useEffect(() => {
    if (draft === null) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setDraft(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [draft]);

  // Mover: listeners de janela durante o gesto — o cursor sai do traço fino
  // imediatamente, então os eventos não chegariam à camada (pointer-events none).
  useEffect(() => {
    if (moving === null) return undefined;
    function onWinMove(event: MouseEvent): void {
      const m = movingRef.current;
      const p = toNorm(event.clientX, event.clientY);
      if (m === null || p === null) return;
      setMoving({
        ...m,
        preview: translateGeometry(m.origin, p.x - m.startNorm.x, p.y - m.startNorm.y),
      });
    }
    function onWinUp(event: MouseEvent): void {
      const m = movingRef.current;
      setMoving(null);
      if (m === null) return;
      const dragged = Math.hypot(event.clientX - m.downClient.x, event.clientY - m.downClient.y);
      if (dragged >= ANNOTATION_MIN_DRAG_SCREEN_PX) onMove(m.id, m.preview);
    }
    window.addEventListener('mousemove', onWinMove);
    window.addEventListener('mouseup', onWinUp);
    return () => {
      window.removeEventListener('mousemove', onWinMove);
      window.removeEventListener('mouseup', onWinUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- liga/desliga pelo gesto
  }, [moving !== null]);

  function onSurfaceMouseDown(event: React.MouseEvent): void {
    if (drawTool === null || event.button !== 0) return;
    // O gesto é do desenho — não vira pan do viewport.
    event.stopPropagation();
    const p = toNorm(event.clientX, event.clientY);
    if (p === null) return;
    onSelect(null);
    setDraft({ start: p, current: p, downClient: { x: event.clientX, y: event.clientY } });
  }

  function onSurfaceMouseMove(event: React.MouseEvent): void {
    if (draft === null) return;
    const p = toNorm(event.clientX, event.clientY);
    if (p === null) return;
    setDraft({ ...draft, current: p });
  }

  function onSurfaceMouseUp(event: React.MouseEvent): void {
    if (draft === null || drawTool === null || size === null) return;
    setDraft(null);
    const dragged = Math.hypot(
      event.clientX - draft.downClient.x,
      event.clientY - draft.downClient.y,
    );
    if (dragged < ANNOTATION_MIN_DRAG_SCREEN_PX) return;
    const geometry =
      drawTool === 'arrow'
        ? arrowFromDrag(draft.start, draft.current)
        : circleFromDrag(draft.start, draft.current, size);
    if (geometry !== null) onDraw(drawTool, drawColor, geometry);
  }

  function onShapeMouseDown(event: React.MouseEvent, annotation: MesaAnnotationData): void {
    if (drawTool !== null || event.button !== 0) return;
    // Selecionar/mover não vira pan do viewport.
    event.stopPropagation();
    const p = toNorm(event.clientX, event.clientY);
    onSelect(annotation.id);
    if (p === null) return;
    setMoving({
      id: annotation.id,
      origin: annotation.geometry,
      preview: annotation.geometry,
      startNorm: p,
      downClient: { x: event.clientX, y: event.clientY },
    });
  }

  const drawing = drawTool !== null;
  const draftGeometry =
    draft !== null && size !== null
      ? drawTool === 'arrow'
        ? arrowFromDrag(draft.start, draft.current)
        : circleFromDrag(draft.start, draft.current, size)
      : null;

  return (
    <div ref={wrapperRef} className="annotation-layer" data-testid="annotation-layer">
      {size !== null && (
        // Superfície de desenho por arrasto — sem role interativa semântica
        // (mesmo padrão justificado da RulerLayer); Esc cancela o desenho.
        <svg
          ref={svgRef}
          className={
            drawing ? 'annotation-surface annotation-surface--drawing' : 'annotation-surface'
          }
          viewBox={`0 0 ${size.width} ${size.height}`}
          preserveAspectRatio="xMidYMid meet"
          role="application"
          aria-label={ptBR.workspaceCaso.annotateLayerLabel}
          data-testid="annotation-surface"
          onMouseDown={onSurfaceMouseDown}
          onMouseMove={onSurfaceMouseMove}
          onMouseUp={onSurfaceMouseUp}
          onMouseLeave={() => setDraft(null)}
        >
          {annotations.map((annotation) => {
            const selected = annotation.id === selectedId;
            const geometry =
              moving !== null && moving.id === annotation.id ? moving.preview : annotation.geometry;
            const inert = drawing || shapesInert;
            return (
              <g
                key={annotation.id}
                className={inert ? 'annotation-shape annotation-shape--inert' : 'annotation-shape'}
                data-testid={`annotation-shape-${annotation.id}`}
                data-tool={annotation.tool}
                data-color={annotation.color}
                data-selected={selected || undefined}
                onMouseDown={(event) => onShapeMouseDown(event, annotation)}
              >
                {selected && (
                  <ShapeSvg
                    geometry={geometry}
                    color={ANNOTATION_COLOR_HEX[annotation.color]}
                    size={size}
                    variant="halo"
                  />
                )}
                <ShapeSvg
                  geometry={geometry}
                  color={ANNOTATION_COLOR_HEX[annotation.color]}
                  size={size}
                />
                <HitSvg geometry={geometry} size={size} />
              </g>
            );
          })}
          {draftGeometry !== null && (
            <g data-testid="annotation-draft" className="annotation-shape--inert">
              <ShapeSvg
                geometry={draftGeometry}
                color={ANNOTATION_COLOR_HEX[drawColor]}
                size={size}
                variant="dashed"
              />
            </g>
          )}
        </svg>
      )}
    </div>
  );
}
